import type { ObjectLookup } from 'src/util';

/**
 * An Error subclass which wraps an underlying error object of unknown type.
 */
export class WrappedError extends Error {
	underlyingError: unknown;

	constructor(message: string, underlyingError: unknown) {
		super(message);

		this.underlyingError = underlyingError;
	}
}

export type ErrorWithInnerError = Error & {
	innerError: Error;
};

export function hasInnerError(e: Error): e is ErrorWithInnerError {
	return e instanceof Error && e['innerError'] && e['innerError'] instanceof Error;
}

/**
 * Takes in an "error-like" object and returns an Error object
 * - If the input is an Error object, it is returned
 * - If the input is a non-zero length string, a new Error is constructed with the input as the message
 * - Otherwise, a new Error object is contructed with a default message
 * @param err The input object, which may or may not be an Error object
 * @param defaultErrorMessage The default message to use when constructing an Error
 * @returns An Error object
 */
export function ensureError(err: unknown, defaultErrorMessage: string = 'Unknown error'): Error {
	if (err instanceof Error)
		return err;
	else if (typeof err === 'string' && err.length > 0)
		return new Error(err);
	else
		return new WrappedError(defaultErrorMessage || 'Unknown error', err);
}

/**
	 * An extension of Error, allows for adding additional properties
	 * to the error object and a flag denoting whether or not to track
	 * the error
	 */
export class CError extends Error {
	name: string;
	message: string;
	// An error is not logged when `log` is exactly false
	log?: boolean;
	stack?: string;
	additionalProperties?: ObjectLookup<any>;

	constructor(message: string, log: boolean = true, name?: string, stack?: string, additionalProperties?: ObjectLookup<any>) {
		super(message);

		this.message = message;
		this.log = log;
		this.name = name || this.name;
		this.stack = stack || this.stack;
		this.additionalProperties = additionalProperties;
	}
}

/**
	 * A version of CError that by default, doesn't log the error
	 * to the server. Used for errors consumed by the end-user.
	 */
export class FriendlyError extends CError {
	constructor(message: string, name?: string) {
		super(message, false, name);
	}
}